import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import { useLocalStore } from 'hooks/useLocalStore';
import { accessibilityParamsStore } from 'stores/root';
import { MetaStore } from 'stores/shared/MetaStore';

import { ObjectFit, PictureLoading } from './config';

import s from './Picture.module.scss';

export type PictureProps = {
  className?: string;
  src: string;
  alt: string;
  width: number;
  height: number;
  loading?: PictureLoading;
  objectFit?: ObjectFit;
};

const Picture: React.FC<PictureProps> = ({
  alt,
  src,
  className,
  objectFit = ObjectFit.CONTAIN,
  loading = PictureLoading.LAZY,
  width,
  height,
  // sources = DEFAULT_SOURCES, //TODO
}) => {
  const { isOpen: isAccessibilityActive } = accessibilityParamsStore;
  const { isPictureBlock } = accessibilityParamsStore.pictureBlockStore;

  const imageRef = React.useRef<HTMLImageElement>(null);
  const store = useLocalStore(() => new MetaStore());

  React.useEffect(() => {
    const ref = imageRef.current;

    store.setLoadedStartMeta();

    if (!ref) {
      return;
    }

    const onLoad = () => {
      if (!ref) {
        return;
      }

      if (
        !src ||
        (ref.naturalWidth === 1 && ref.naturalHeight === 1) ||
        (ref.naturalWidth === 0 && ref.naturalHeight === 0)
      ) {
        store.setLoadedErrorMeta();

        return;
      }
      store.setLoadedSuccessMeta();
    };

    const onError = () => {
      store.setLoadedErrorMeta();
    };

    if (ref.complete) {
      onLoad();

      return;
    }

    ref.addEventListener('load', onLoad);
    ref.addEventListener('error', onError);

    return () => {
      ref.removeEventListener('load', onLoad);
      ref.removeEventListener('error', onError);
    };
  }, [src, store]);

  return (
    <picture
      onError={() => {
        store.setLoadedErrorMeta();
      }}
      className={cn(
        s.picture,
        store.isLoaded && s.picture_loaded,
        store.isError && s.picture_error,
        isAccessibilityActive && s.picture_accessibility,
        className
      )}
    >
      <img
        width={width}
        height={height}
        className={cn(s.picture__src, s[`picture__src-${objectFit}`])}
        ref={imageRef}
        loading={loading}
        srcSet={isPictureBlock ? undefined : src}
        alt={alt}
      />
    </picture>
  );
};

export default observer(Picture);
